import { Box, Checkbox, FormControlLabel, Typography } from "@mui/material";
import { DateTime } from "luxon";
import type { FC, RefObject } from "react";
import { useTranslation } from "react-i18next";
import { SynergyFacilityOptionStartAdornment } from "@/pages/Profile/v2/SynergyCredentials/SynergyFacilityOptionStartAdornment";
import { INK } from "@/theme/tokens";
import { DateInput } from "@/ui/Inputs/DateInput";
import { SystemAutoComplete } from "@/ui/Inputs/SystemAutoComplete/SystemAutoComplete";
import { SystemDropdown } from "@/ui/Inputs/SystemDropDown";
import { InfoIconWithTooltip } from "@/ui/molecules/InfoIconWithTooltip";
import SystemButton from "@/ui/SystemButton/SystemButton";
import type { DepartmentLocation } from "@/utils/models";
import { useMedicalDepartmentPostShiftState } from "./useMedicalDepartmentPostShiftState";

type Props = {
	facility?: DepartmentLocation;
	clickedDate: DateTime;
	onClose?: ((didUpdateOrCreate?: boolean) => void) | ((didUpdateOrCreate?: boolean) => Promise<void>);
	/**
	 * Ref from parent to control did change in inner state.
	 */
	didChangeRef?: RefObject<boolean>;
	/**
	 * Triggered when the didChangeRef is true, and we attempt to close
	 */
	onWarnOnClose?: () => void;
};

const minWidth = "350px";

export const MedicalDepartmentPostShift: FC<Props> = ({ didChangeRef, onWarnOnClose, onClose, clickedDate, facility }) => {
	const { t } = useTranslation();
	const {
		state,
		facilitiesList,
		onSelectedFacilityChange,
		rolesOptions,
		handleRoleChange,
		handleDateChange,
		handleCloseClick,
		handleKeepOpenChange,
		handleSaveClick,
		saveDisabled,
		isLoading,
	} = useMedicalDepartmentPostShiftState({
		didChangeRef,
		onWarnOnClose,
		onClose,
		clickedDate,
		facility,
	});
	return (
		<Box
			data-testid="post-shift-panel"
			sx={{
				height: "fit-content",
				display: "flex",
				flexDirection: "column",
				gap: 3,
				padding: 2,
				minHeight: "300px",
			}}
		>
			<Box
				sx={{
					display: "flex",
					gap: 1,
					alignItems: "center",
				}}
			>
				<Box
					data-testid="post-shift-date"
					sx={{
						display: "flex",
						gap: 0.5,
						alignItems: "end",
					}}
				>
					<DateInput value={state.currentDate} onChange={handleDateChange} minDate={DateTime.now().startOf("week")} />
					<Typography
						sx={{
							color: INK.SECONDARY,
						}}
					>
						({state.currentDate.toFormat("ccc")})
					</Typography>
				</Box>
			</Box>
			<Box
				sx={{
					flex: 1,
					display: "flex",
					flexDirection: "column",
					gap: 2,
				}}
			>
				<SystemAutoComplete
					dataTestId="post-shift-location"
					options={facilitiesList}
					value={state.selectedFacility}
					onChange={onSelectedFacilityChange}
					textFieldProps={{
						inputSx: { width: minWidth },
						placeholder: t("med.dept.facility.placeholder"),
						label: t("facility"),
						reserveHeightForHelperText: false,
					}}
					justifyContent={"start"}
					getOptionStartAdornment={(option) => <SynergyFacilityOptionStartAdornment facilityType={option.value.departmentType} />}
				/>
				<Box
					sx={{
						display: "flex",
						gap: 1,
						alignItems: "center",
					}}
				>
					<SystemDropdown
						data-testid="post-shift-role"
						label={t("choose.roles")}
						allOption={t("med.dept.all.roles")}
						items={rolesOptions}
						selectedItems={state.roles}
						onChangeMultiple={handleRoleChange}
						multiple
						maxMenuHeight={140}
						selectSx={{
							width: minWidth,
						}}
					/>
					<InfoIconWithTooltip title={t("med.dept.roles.tooltip")} />
				</Box>
			</Box>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
				}}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "start",
						gap: 1,
					}}
				>
					<FormControlLabel
						data-testid="post-shift-create-more-toggle"
						onChange={handleKeepOpenChange}
						sx={{
							margin: 0,
							padding: 0,
						}}
						control={
							<Checkbox
								sx={{
									p: 0.25,
								}}
							/>
						}
						label={
							<Typography
								sx={{
									fontSize: "14px",
									color: INK.SECONDARY,
								}}
							>
								{t("med.dept.post.more")}
							</Typography>
						}
					/>
					<InfoIconWithTooltip title={t("med.dept.post.more.tooltip")} />
				</Box>
				<Box
					sx={{
						justifyContent: "end",
						display: "flex",
						alignItems: "center",
						gap: 1,
					}}
				>
					<SystemButton data-testid="post-shift-cancel-button" buttonVariant={"secondary"} onClick={handleCloseClick} disabled={isLoading}>
						{t("cancel")}
					</SystemButton>
					<SystemButton
						data-testid="post-shift-create-button"
						disabled={saveDisabled}
						tooltip={saveDisabled ? t("med.dept.select.facility.roles") : undefined}
						buttonVariant={"primary"}
						onClick={handleSaveClick}
						loading={isLoading}
					>
						{t("save")}
					</SystemButton>
				</Box>
			</Box>
		</Box>
	);
};
